import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, Card } from "../components/ui-bj/Primitives";
import { ErrorState } from "../components/ui-bj/States";
import { useEventsPage, fetchEventTypes, fetchAllRecords } from "../hooks/useAirtable";
import { str, fmtDateTime, timeAgo } from "../lib/airtableHelpers";
import ExportButtons from "../components/ui-bj/ExportButtons";
import PeriodScopeBar, { usePeriodFilter } from "../components/ui-bj/PeriodScopeBar";

const EVENT_TONE = {
  login: "#14281E",
  signup: "#14281E",
  page_view: "#6E5022",
  click: "#6E5022",
  document_upload: "#6E5022",
  payment: "#14281E",
  error: "#5C1A24",
  default: "#5E4D40",
};
const toneFor = (t) => EVENT_TONE[(t || "").toLowerCase()] || EVENT_TONE.default;

const toRow = (r) => {
  const f = r.fields || {};
  return {
    id: r.id,
    timestamp: f["Timestamp"],
    type: str(f["Event Type"]),
    clientId: str(f["Client ID"]),
    page: str(f["Page"]),
    session: str(f["Session ID"]),
    meta: str(f["Metadata"]),
  };
};

const PAGE_SIZE = 50;

const EventsPage = () => {
  const [typeFilter, setTypeFilter] = useState("All");
  const { period, setPeriod, scope, setScope, filterByDate } = usePeriodFilter("all");
  const [typeInfo, setTypeInfo] = useState({ types: [], total: null });
  const events = useEventsPage({
    eventType: typeFilter === "All" ? null : typeFilter,
    month: scope === "period" ? period : null,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    fetchEventTypes().then(setTypeInfo).catch(() => {});
  }, []);

  const rows = useMemo(() => events.records.map(toRow), [events.records]);
  const typeOptions = ["All", ...typeInfo.types];

  const exportRows = async () =>
    (await fetchAllRecords("events"))
      .map(toRow)
      .filter((r) => filterByDate(r.timestamp) && (typeFilter === "All" || r.type === typeFilter))
      .sort((x, y) => (y.timestamp || "").localeCompare(x.timestamp || ""));

  if (events.error && events.page === 1 && !rows.length) return <ErrorState message={events.error} onRetry={events.reset} />;

  const visible = rows;
  const first = (events.page - 1) * PAGE_SIZE + 1;

  return (
    <div data-testid="events-page">
      <PageHeader
        eyebrow="DASHBOARD / EVENT STREAM"
        title="Event Stream"
        subtitle={`${typeInfo.total !== null ? `${typeInfo.total.toLocaleString()} events in total` : "Event stream"} • ${scope === "period" ? "filtered by month" : "all-time view"} • Newest first, ${PAGE_SIZE} per page.`}
        right={
          <div className="flex items-center gap-3 flex-wrap">
            <PeriodScopeBar scope={scope} setScope={setScope} period={period} setPeriod={setPeriod} testidPrefix="events-scope" />
            <ExportButtons
              title="Event Stream"
              filename="events"
              testidPrefix="export-events"
              columns={[
                { key: "timestamp", label: "Timestamp" },
                { key: "type", label: "Event Type" },
                { key: "clientId", label: "Client ID" },
                { key: "page", label: "Page" },
                { key: "session", label: "Session ID" },
                { key: "meta", label: "Metadata" },
              ]}
              getRows={exportRows}
            />
          </div>
        }
      />

      {typeOptions.length > 1 && (
        <Card className="mb-5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="bj-label mr-2">Event Type</span>
            {typeOptions.map((t) => (
              <button
                key={t}
                data-testid={`events-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setTypeFilter(t)}
                className={`px-3 py-1.5 border font-heading tracking-widest text-[0.75rem] uppercase ${typeFilter === t ? "border-[#2A1C15] bg-[#2A1C15] text-[#DCCFB9]" : "border-[rgba(42,28,21,0.18)] text-[#2A1C15] hover:border-[#2A1C15]"}`}
              >
                {t}
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[1000px]">
          <thead>
            <tr className="text-left">
              {["When", "Type", "Client", "Page", "Session", "Metadata"].map((h) => (
                <th key={h} className="bj-table-header py-2 pr-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {events.loading && (
              <tr><td colSpan={6} data-testid="events-loading" className="py-10 text-center text-[#5E4D40] text-sm">Loading events…</td></tr>
            )}
            {!events.loading && events.error && (
              <tr><td colSpan={6} data-testid="events-error" className="py-10 text-center text-[#5C1A24] text-sm">{events.error}</td></tr>
            )}
            {!events.loading && !events.error && visible.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-[#5E4D40] text-sm">No events for this filter.</td></tr>
            )}
            {!events.loading && visible.map((e, i) => (
              <tr key={e.id} data-testid={`event-row-${i}`} className="bj-table-row">
                <td className="py-3 pr-3 text-sm whitespace-nowrap">
                  <div className="text-[#2A1C15] bj-tnum">{fmtDateTime(e.timestamp)}</div>
                  <div className="text-[#5E4D40] text-[0.75rem] tracking-widest uppercase font-heading">{timeAgo(e.timestamp)}</div>
                </td>
                <td className="py-3 pr-3">
                  <span
                    className="inline-flex items-center px-2 py-0.5 border text-[0.75rem] tracking-widest uppercase font-heading"
                    style={{ color: toneFor(e.type), borderColor: `${toneFor(e.type)}66` }}
                  >
                    {e.type}
                  </span>
                </td>
                <td className="py-3 pr-3 text-[#6E5022] text-sm bj-tnum">{e.clientId}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm">{e.page}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-xs bj-tnum truncate max-w-[160px]" title={e.session}>{e.session}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-xs truncate max-w-[260px]" title={e.meta}>{e.meta}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div data-testid="events-pager" className="flex items-center justify-between gap-4 pt-5 text-sm">
          <span data-testid="events-page-range" className="text-[#5E4D40] bj-tnum">
            {visible.length ? `Showing ${first.toLocaleString()}–${(first + visible.length - 1).toLocaleString()}` : `Page ${events.page}`}
          </span>
          <div className="flex items-center gap-2">
            <button data-testid="events-prev-page" onClick={events.prev} disabled={events.loading || events.page === 1} className="pr-btn-outline">Previous</button>
            <span data-testid="events-page-number" className="px-2 bj-tnum">Page {events.page}</span>
            <button data-testid="events-next-page" onClick={events.next} disabled={events.loading || !events.nextOffset} className="pr-btn-outline">Next</button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default EventsPage;
